import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { userApplicationApi } from '@/lib/api/questionnaireApi';
import { QuestionProps, UserApplicationData } from '@/types/questionnaire';
import { router, usePage } from '@inertiajs/react';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

const getOptionLabel = (question: QuestionProps, value: string) => {
  if (!('options' in question) || !question.options) return value;
  const option = question.options.find((opt) => opt.value === value);
  return option?.label || value;
};

const useApplicationData = (userId: number | null) => {
  return useQuery<UserApplicationData>({
    queryKey: ['user-application', userId],
    queryFn: async () => {
      if (!userId) throw new Error('No user ID provided');
      return await userApplicationApi.getById(userId);
    },
    enabled: !!userId,
  });
};
interface ApplicationProps {
  userId: number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function Application({
  userId,
  open,
  onOpenChange,
}: ApplicationProps) {
  const { errors } = usePage().props;
  const { data: applicationData, isLoading } = useApplicationData(userId);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[85vh] p-0 flex flex-col overflow-hidden">
          <div className="px-6 py-4 border-b flex-shrink-0">
            <DialogTitle className="text-xl font-semibold">
              Application Review
            </DialogTitle>
            {applicationData && (
              <div className="mt-3 space-y-1">
                <div className="text-sm text-gray-900 font-medium">
                  {`${applicationData.user.first_name} ${applicationData.user.last_name}`}
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-600">
                  <span className="capitalize">
                    {applicationData.user.category.replace('_', ' ')}{' '}
                    {applicationData.user.role}
                  </span>
                  {applicationData.completed_at && (
                    <>
                      <span className="text-gray-400">•</span>
                      <span>
                        Submitted{' '}
                        {new Date(
                          applicationData.completed_at
                        ).toLocaleDateString()}
                      </span>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-4">
            {isLoading && (
              <div className="text-center py-8 text-gray-500">
                Loading application...
              </div>
            )}

            {applicationData && applicationData.sections && (
              <div className="space-y-6">
                {applicationData.sections.map((section, sectionIndex) => (
                  <div key={section.id}>
                    <div className="mb-4">
                      <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                        <span className="text-blue-600 text-sm font-normal">
                          Section {sectionIndex + 1}
                        </span>
                        {section.section_name}
                      </h3>
                      {section.description && (
                        <p className="text-sm text-gray-500 mt-1">
                          {section.description}
                        </p>
                      )}
                    </div>

                    <div className="space-y-4 ml-4">
                      {section.questions.map((question) => (
                        <div key={question.id} className="group">
                          <div className="flex items-start gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2 flex-shrink-0" />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-700 mb-1">
                                {question.label}
                                {question.required && (
                                  <span className="text-red-500 ml-0.5 text-xs">
                                    *
                                  </span>
                                )}
                              </p>
                              <div className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md">
                                {question.response_value ? (
                                  Array.isArray(question.response_value) ? (
                                    <ul className="space-y-1">
                                      {question.response_value.map(
                                        (item, idx) => (
                                          <li
                                            key={idx}
                                            className="flex items-center gap-2"
                                          >
                                            <span className="text-gray-400">
                                              →
                                            </span>
                                            {getOptionLabel(question, item)}
                                          </li>
                                        )
                                      )}
                                    </ul>
                                  ) : question.question_type === 'text' ? (
                                    <div className="whitespace-pre-wrap">
                                      {question.response_value}
                                    </div>
                                  ) : question.question_type === 'select' ||
                                    question.question_type === 'radio' ? (
                                    getOptionLabel(
                                      question,
                                      question.response_value
                                    )
                                  ) : (
                                    question.response_value
                                  )
                                ) : (
                                  <span className="text-gray-400 italic">
                                    No response provided
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {sectionIndex < applicationData.sections.length - 1 && (
                      <div className="border-b border-gray-200 mt-6" />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {applicationData && (
            <div className="px-6 py-3 border-t flex justify-between items-center flex-shrink-0">
              <div className="text-xs text-gray-500">
                Application ID: #{applicationData.user.id}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  Close
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => setShowRejectDialog(true)}
                >
                  Reject
                </Button>
                <Button
                  onClick={() =>
                    router.patch(
                      `/admin/users/${applicationData.user.id}/approve`,
                      {},
                      {
                        onSuccess: () => {
                          onOpenChange(false);
                        },
                      }
                    )
                  }
                >
                  Approve
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {showRejectDialog && (
        <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Reject Application</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <label className="text-sm font-medium text-gray-700 block mb-2">
                Reason for rejection
              </label>
              <Textarea
                placeholder="Please provide a clear reason for rejection..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
              />
              {errors?.reason && (
                <p className="text-red-500 text-sm mt-1">{errors.reason}</p>
              )}
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setShowRejectDialog(false);
                  setRejectReason('');
                }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  router.patch(
                    `/admin/users/${applicationData?.user.id}/reject`,
                    { reason: rejectReason },
                    {
                      onSuccess: () => {
                        setShowRejectDialog(false);
                        setRejectReason('');
                        onOpenChange(false);
                      },
                    }
                  );
                }}
              >
                Confirm Rejection
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
